import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Calendar, Clock, ChevronDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const ORBITRANK_CLIENT_ID = "1ff7ac35-bc13-4a46-9290-59cd4def086d";
import logoFull from "@/assets/orbitrank-logo-v3.png";
import logoMark from "@/assets/orbitrank-mark.png";
import "@/styles/landing.css";
import imgAutomatedSeo from "@/assets/blog/automated-seo.jpg";
import imgAiCitation from "@/assets/blog/ai-citation.jpg";
import imgSeoAutomation from "@/assets/blog/seo-automation.jpg";
import imgAiRanking from "@/assets/blog/ai-ranking.jpg";

const SLUG_IMAGES: Record<string, string> = {
  "automated-seo-rank-more-do-less": imgAutomatedSeo,
  "getting-cited-by-ai": imgAiCitation,
  "seo-automation-explained": imgSeoAutomation,
  "ai-search-ranking-factors": imgAiRanking,
};

// New generated posts can contain multi-MB base64 images inside JSON-LD.
// Strip those payloads before React inserts the body so article pages don't
// spend seconds downloading/parsing duplicate image data.
function sanitizeArticleHtml(html: string): string {
  return html
    .replace(/<script[^>]*type=["']application\/ld\+json["'][^>]*>[\s\S]*?<\/script>/gi, "")
    .replace(/<figure\b[^>]*>\s*<img\b[^>]*class=["'][^"']*\bhero-image\b[^"']*["'][^>]*>\s*(?:<figcaption[\s\S]*?<\/figcaption>\s*)?<\/figure>\s*/gi, "")
    .replace(/<img\b[^>]*class=["'][^"']*\bhero-image\b[^"']*["'][^>]*>\s*/gi, "");
}

const NAV_LINKS = [
  { label: "Home", href: "/" },
  { label: "Features", href: "/#features" },
  { label: "How It Works", href: "/#how-it-works" },
  { label: "Pricing", href: "/#pricing" },
  { label: "Resources", href: "#", caret: true },
  { label: "Blog", href: "/blog", active: true },
];

type Article = {
  slug: string;
  category: string;
  accent: string;
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
  body: React.ReactNode;
  featuredImage?: string;
};


const launchCta = (label = "Launch My SEO") => (
  <a
    href="/activate"
    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-[14px] font-semibold text-[#04060A] bg-[#00FF88] hover:bg-[#1aff96] transition-all whitespace-nowrap"
    style={{ boxShadow: "0 0 0 1px rgba(0,255,136,0.4), 0 8px 24px rgba(0,255,136,0.25)" }}
  >
    {label} <ArrowRight className="w-4 h-4" />
  </a>
);

const A: React.FC<{ to: string; children: React.ReactNode }> = ({ to, children }) => (
  <Link to={to} style={{ color: "#A78BFA", textDecoration: "underline", textUnderlineOffset: 3 }}>{children}</Link>
);
const Aext: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
  <a href={href} style={{ color: "#00FF88", textDecoration: "underline", textUnderlineOffset: 3 }}>{children}</a>
);

const ARTICLES: Record<string, Article> = {
  "automated-seo-rank-more-do-less": {
    slug: "automated-seo-rank-more-do-less",
    category: "SEO Automation",
    accent: "#A78BFA",
    title: "The Power of Automated SEO: Rank More, Do Less",
    excerpt: "Why manual SEO is too slow for 2026 — and how automated systems deploy, optimize, and grow visibility while you sleep.",
    date: "May 14, 2026",
    readTime: "10 min read",
    body: (
      <>
        <p>SEO used to be a checklist. Pick a keyword, write a page, build a few links, refresh the meta tags every quarter, and pray. That model worked when search engines moved slowly and competition was thin. In 2026, neither of those things is true. Google ships ranking updates almost every week. ChatGPT, Perplexity, and Gemini are quietly absorbing the long tail of search. And every competitor in your category is publishing more content, faster, than you can keep up with by hand.</p>

        <p>This is the gap that automated SEO closes. Not by replacing strategy — strategy still matters — but by removing every repetitive task that used to drown teams in operational work. The result is simple: <strong>more pages, more signals, more visibility, less manual effort.</strong></p>

        <h2>Why manual SEO breaks in 2026</h2>

        <p>The traditional SEO workflow has roughly six steps: keyword research, content briefing, writing, on-page optimization, publishing, and monitoring. Each step is a hand-off. Each hand-off is a delay. A single piece of content can take two to three weeks to ship — and that is before you account for internal review, design, or technical implementation.</p>

        <p>Now multiply that by the volume modern search demands. To compete in any meaningful category today, you need topical coverage across hundreds of related queries. Manual SEO simply cannot produce that surface area, at that speed, without a team you cannot afford.</p>

        <p>Automation is not a luxury anymore. It is the only way to operate at the speed search now requires.</p>

        <h2>What "automated SEO" actually means</h2>

        <p>The term gets thrown around loosely, so it helps to be precise. Real SEO automation is not a content spinner. It is a connected pipeline that handles the work humans used to do manually:</p>

        <ul>
          <li><strong>Discovery:</strong> finding the keywords and questions your customers actually search for.</li>
          <li><strong>Production:</strong> generating structured, on-brand content that answers those queries with real depth.</li>
          <li><strong>Deployment:</strong> publishing directly to your site with proper schema, internal links, and metadata.</li>
          <li><strong>Indexing:</strong> notifying search engines and AI crawlers the moment a page goes live.</li>
          <li><strong>Monitoring:</strong> tracking rankings, citations, and visibility shifts across both Google and AI engines.</li>
          <li><strong>Optimization:</strong> rewriting and refreshing pages based on real performance data.</li>
        </ul>

        <p>This is the system <Aext href="/">OrbitRank</Aext> runs every day for the businesses on the platform. The point is not to remove humans from SEO — it is to remove the busywork that used to consume them.</p>

        <h2>The compounding advantage of automation</h2>

        <p>Manual SEO produces linear results: one person writes one page in one day. Automated SEO compounds. Each new page deepens topical authority. Each internal link strengthens the surrounding cluster. Each indexed URL gives AI engines a new opportunity to cite you. The output grows faster than the input.</p>

        <p>Within 60 days, most automated SEO systems produce more indexed pages than a manual team would ship in a year. Within 90, the topical depth begins to shift rankings on competitive head terms — the queries you could never realistically target page-by-page.</p>

        <h2>What you stop doing</h2>

        <p>This is usually the moment teams realize what they have been losing to manual work:</p>

        <ul>
          <li>No more weekly keyword spreadsheets.</li>
          <li>No more brief documents bouncing between strategists and writers.</li>
          <li>No more "is this published yet?" Slack threads.</li>
          <li>No more guessing whether Google has indexed a page.</li>
          <li>No more month-end reports built by hand.</li>
        </ul>

        <p>That time goes back into the parts of the business that genuinely need humans: product, partnerships, brand, customer experience.</p>

        <h2>What automation cannot replace</h2>

        <p>To be clear: automation does not replace judgment. You still need a point of view on positioning, audience, and tone. You still need to decide which markets matter. You still need to review the output of the system and steer it. Read <A to="/blog/seo-automation-explained">SEO Automation Explained: What Runs, What Doesn't</A> for a clear breakdown of where the line sits.</p>

        <p>But everything downstream of those decisions — the production, the publishing, the indexing, the tracking — is exactly where machines outperform humans. Consistently, cheaply, and at scale.</p>

        <h2>How AI visibility fits in</h2>

        <p>One reason automated SEO matters more now than five years ago is that search itself has split. Google is no longer the only destination. ChatGPT, Gemini, and Perplexity answer billions of queries every month, and they choose which businesses to cite based on signals that update in near real time. If your content is not being produced, structured, and refreshed constantly, you will not show up in those answers. Read <A to="/blog/getting-cited-by-ai">Getting Cited by AI: The New SEO Frontier</A> to see exactly how AI engines decide who gets surfaced.</p>

        <h2>What an automated SEO day looks like</h2>

        <p>On any given morning inside OrbitRank, a new article is generated for your business based on a real query your audience is searching. It is published to your site with schema markup, internal links to relevant existing pages, and a clean meta block. It is submitted to Google and pinged out to the AI crawlers. By the time you check your dashboard, ranking shifts from yesterday's pages are already in.</p>

        <p>You did not write a brief. You did not edit a draft. You did not push to production. The system did all of it — and it will do it again tomorrow.</p>

        <h2>Getting started</h2>

        <p>The biggest mistake teams make with SEO automation is treating it like a side project. It is a primary growth channel that runs continuously. The earlier it starts, the more compounding you get. If you want to see what an automated SEO engine actually produces for a real business, the fastest path is to launch one for your own domain and watch the first week of output.</p>

        <p>To dig deeper into how AI engines specifically choose what to cite, read <A to="/blog/ai-search-ranking-factors">AI Search Ranking Factors You Can't Ignore</A>. To understand where automation ends and human strategy begins, read <A to="/blog/seo-automation-explained">SEO Automation Explained</A>.</p>
      </>
    ),
  },

  "getting-cited-by-ai": {
    slug: "getting-cited-by-ai",
    category: "AI Search",
    accent: "#38BDF8",
    title: "Getting Cited by AI: The New SEO Frontier",
    excerpt: "ChatGPT, Gemini, and Perplexity are quietly becoming the new front page of search. Here's how to make sure your business gets cited inside their answers.",
    date: "May 9, 2026",
    readTime: "9 min read",
    body: (
      <>
        <p>For two decades, ranking on Google was the entire game. If you owned the top three blue links for the queries that mattered to your business, you owned the market. That game is not over — but it is no longer the only game.</p>

        <p>Today, a growing share of search happens inside AI engines. ChatGPT answers questions before the user ever opens a browser tab. Perplexity returns a paragraph with citations instead of a list of links. Gemini summarizes the web inside Google itself. And in each of these answers, two or three businesses get mentioned by name. The rest become invisible.</p>

        <p>This is the new SEO frontier: not ranking, but <strong>being cited</strong>.</p>

        <h2>How AI engines actually find businesses</h2>

        <p>AI engines do not invent the businesses they cite. They surface them from the open web — from indexed pages, structured data, review platforms, directories, and authoritative third-party sources. Every citation traces back to content that already exists. The question is whether your content exists in a form an AI can confidently quote.</p>

        <p>This matters because AI engines optimize for one thing above all: <em>trust</em>. They will not cite a business they cannot verify. They will not cite a page that contradicts itself. They will not cite content that has no clear authorship, no schema, no internal consistency. Hallucinating a business name is the single biggest reputational risk an AI engine faces, so they err heavily on the side of citing sources that look provably real.</p>

        <h2>The four signals that earn AI citations</h2>

        <p>From the patterns we see across the businesses growing fastest inside AI search, four signals consistently move the needle:</p>

        <h3>1. Topical depth</h3>
        <p>One brilliant page is not enough. AI engines want to see that you have written about a topic from multiple angles. A plumber who has one "services" page is invisible. A plumber with thirty pages covering specific issues, neighborhoods, and seasonal problems becomes the obvious citation. Depth is what makes you the safe answer.</p>

        <h3>2. Structured content</h3>
        <p>AI parsers love structure. Clear headings. Short paragraphs. Lists. FAQ blocks. Schema markup. The more legible your content is to a machine, the more likely it is to get extracted into an answer. Walls of text get skipped.</p>

        <h3>3. Consistency across the web</h3>
        <p>If your business name, address, services, and positioning say one thing on your site and something different on Yelp, Google Business Profile, or industry directories, AI engines will downgrade you. Citations go to businesses that look the same everywhere.</p>

        <h3>4. Freshness</h3>
        <p>AI engines weight recently updated content heavily. A page from 2021 will lose to a page from last month, all else equal. This is why automated, continuous publishing has become so powerful — it keeps your topical surface fresh without requiring constant human effort. See <A to="/blog/automated-seo-rank-more-do-less">The Power of Automated SEO</A> for why this matters at the operational level.</p>

        <h2>Why traditional SEO is not enough</h2>

        <p>Plenty of businesses rank well on Google and still get ignored by ChatGPT. The reason is that ranking and citation are scored differently. Google rewards link authority, dwell time, and historical trust. AI engines reward topical coverage, structural clarity, and citation-worthiness in a single paragraph of answer space.</p>

        <p>A page can be the #1 Google result and still not get cited by an AI, simply because the content is too long, too unstructured, or too brand-focused to be quoted cleanly. Conversely, a less authoritative page that is tightly written, clearly structured, and topically deep can absolutely beat it inside an AI answer.</p>

        <h2>What you should actually do</h2>

        <p>If you want to start earning AI citations, the playbook is straightforward:</p>

        <ul>
          <li><strong>Cover the full topic</strong> — not just the obvious head terms, but the questions, edge cases, and adjacent problems your customers actually ask.</li>
          <li><strong>Write for extractability</strong> — short paragraphs, real headings, FAQ schema, factual statements that can stand on their own.</li>
          <li><strong>Keep your facts aligned</strong> — your site, your Google Business Profile, your directory listings, and your social bios should all agree.</li>
          <li><strong>Publish continuously</strong> — freshness is a ranking signal inside every major AI engine. Pages that stop updating drift out of citations.</li>
          <li><strong>Build internal authority</strong> — link related pages together so AI crawlers can map your topical depth quickly.</li>
        </ul>

        <p>This is the exact playbook <Aext href="/">OrbitRank</Aext> runs automatically. The platform generates topically deep, structurally clean content for your business every day, deploys it with the right schema, and tracks which pages are being surfaced by AI engines so you can see the citations as they happen.</p>

        <h2>The long-term shift</h2>

        <p>Within the next few years, "search visibility" will mean something different than it does today. The brands that win will be the ones cited inside the answer — not just the ones ranking below it. Some of those brands will be the same names that dominated Google. Many will not. The opening is now, and it favors the businesses that move first.</p>

        <p>For a deeper look at the specific ranking signals AI engines use behind the scenes, read <A to="/blog/ai-search-ranking-factors">AI Search Ranking Factors You Can't Ignore</A>.</p>
      </>
    ),
  },

  "seo-automation-explained": {
    slug: "seo-automation-explained",
    category: "SEO Automation",
    accent: "#38BDF8",
    title: "SEO Automation Explained: What Runs, What Doesn't",
    excerpt: "A practical breakdown of what parts of SEO can realistically run on autopilot — and what still needs a human in the loop.",
    date: "Apr 28, 2026",
    readTime: "8 min read",
    body: (
      <>
        <p>"SEO automation" is one of those phrases that gets used to mean almost anything. For some teams it means scheduling a few social posts. For others it means a fully autonomous content engine producing dozens of pages a week. The reality sits in between, and the line between what should run on autopilot and what should not is more interesting than the hype suggests.</p>

        <p>This is the honest map.</p>

        <h2>What runs perfectly on autopilot</h2>

        <p>These are the parts of SEO where machines are not just faster than humans — they are more reliable. Automating them removes errors, not just labor.</p>

        <h3>1. Keyword discovery</h3>
        <p>Pulling search volumes, ranking difficulty, and intent signals across thousands of queries is the kind of work humans have always done poorly. Spreadsheets get outdated the moment they are saved. Automated systems pull this data fresh, every day, and surface only the queries that actually map to your business.</p>

        <h3>2. Content generation</h3>
        <p>Drafting on-brand, topically deep articles based on a real query is now within reach of modern AI when paired with the right structure. The key word is <em>structure</em> — good automated content is not "AI writing whatever it wants." It is a tightly bounded pipeline that enforces tone, fact constraints, schema, and internal linking. Done right, the output is indistinguishable from a careful human draft. See <A to="/blog/automated-seo-rank-more-do-less">The Power of Automated SEO</A> for what that looks like in practice.</p>

        <h3>3. On-page optimization</h3>
        <p>Title tags, meta descriptions, headings, image alts, schema markup, internal links — all of this is rules-based work. Machines apply rules consistently. Humans forget.</p>

        <h3>4. Publishing and deployment</h3>
        <p>Pushing a finished page to your CMS, applying the right template, and going live is plumbing work. There is no creative judgment in it. Automate it.</p>

        <h3>5. Indexing and submission</h3>
        <p>Notifying Google, pinging AI crawlers, and submitting URLs to the right endpoints is purely mechanical. A human doing this by hand is a human wasting their day.</p>

        <h3>6. Monitoring</h3>
        <p>Tracking rankings, traffic, click-through rates, citations, and core web vitals is something machines do at a scale and frequency no human team can match. Real-time monitoring catches problems before they cost you visibility.</p>

        <h2>What still needs a human</h2>

        <p>Automation breaks down when judgment is required. These are the places where you absolutely still need a person in the loop.</p>

        <h3>1. Positioning and brand voice</h3>
        <p>No machine can decide what your business is, who it serves, and how it sounds. Those decisions need to be made by a human and then fed into the system. Once they exist, automation can express them at scale — but it cannot invent them.</p>

        <h3>2. Strategic priorities</h3>
        <p>Which markets matter? Which products do you push? Which competitors do you actively try to beat? These are business decisions, not SEO decisions, and they belong to humans.</p>

        <h3>3. Editorial taste</h3>
        <p>The system will publish dozens of pages a week. Some of them will be excellent. A few will be only good. A human reviewing the worst ones and pulling the lever to regenerate keeps quality climbing over time.</p>

        <h3>4. Crisis response</h3>
        <p>When something breaks — a bad ranking drop, a manual action, a tone-deaf piece of content surfacing in the wrong context — humans need to step in. Automation handles steady state, not exceptions.</p>

        <h3>5. Real-world relationships</h3>
        <p>Press, partnerships, podcast appearances, expert interviews, customer stories — these still produce some of the most powerful SEO assets on the web, and none of them can be generated by a machine. They require humans doing human things.</p>

        <h2>The hybrid that actually works</h2>

        <p>The strongest SEO operations today are not "all human" or "all AI." They are hybrids in which humans set the strategy and review the edges, and machines do every repetitive operation in between. This is exactly how <Aext href="/">OrbitRank</Aext> is designed: you tell the system who you are and who you serve, and it handles discovery, production, deployment, indexing, and monitoring on a continuous loop.</p>

        <p>The output is a stack of indexed, optimized, freshly-updated pages going live every day — without your team writing briefs, editing drafts, or pushing to production. The human work shifts from operating SEO to <em>steering</em> it.</p>

        <h2>The mistakes to avoid</h2>

        <ul>
          <li><strong>Automating before you have positioning.</strong> Garbage in, garbage out. Define who you are before you turn the engine on.</li>
          <li><strong>Treating automation as set-and-forget.</strong> You still need to review outputs and adjust direction. The system gets better when steered.</li>
          <li><strong>Ignoring the human moats.</strong> Press, partnerships, and authentic expertise are not optional. They are what makes automated content credible.</li>
          <li><strong>Confusing volume with quality.</strong> Publishing more bad pages does not move rankings. Publishing more <em>structurally sound, topically deep</em> pages does.</li>
        </ul>

        <h2>Where to go from here</h2>

        <p>If you want to understand the underlying ranking signals the automated pipeline is optimizing for, read <A to="/blog/ai-search-ranking-factors">AI Search Ranking Factors You Can't Ignore</A>. If you want to see how this same pipeline gets your business cited inside ChatGPT and Perplexity answers, read <A to="/blog/getting-cited-by-ai">Getting Cited by AI: The New SEO Frontier</A>.</p>

        <p>SEO automation is not magic. It is a clear division of labor between humans and machines, with each side doing what it is genuinely good at. When that division is set up correctly, the results compound faster than any manual team can match.</p>
      </>
    ),
  },

  "ai-search-ranking-factors": {
    slug: "ai-search-ranking-factors",
    category: "AI Search",
    accent: "#00FF88",
    title: "AI Search Ranking Factors You Can't Ignore",
    excerpt: "The signals ChatGPT, Gemini, and Perplexity actually use to decide which businesses to cite — and how to optimize for them.",
    date: "Apr 24, 2026",
    readTime: "9 min read",
    body: (
      <>
        <p>Traditional SEO had a fairly stable list of ranking factors: backlinks, on-page relevance, dwell time, page speed, mobile usability, freshness, schema. The weights shifted over time, but the list was knowable. AI search is different. The ranking factors are newer, less documented, and changing faster — but the underlying patterns are already clear enough to optimize against.</p>

        <p>If you only have time to focus on a handful of signals, focus on these.</p>

        <h2>1. Topical authority over keyword authority</h2>

        <p>Google has always rewarded keyword-level relevance. AI engines reward something closer to <em>topical mastery</em>. They want to cite the business that has clearly covered an entire subject from every angle — not the one that ranks for a single query.</p>

        <p>Practically, this means depth beats breadth. Twenty pages on plumbing emergencies in your service area will outperform one page targeting "plumber [city]" almost every time inside an AI answer. The same logic applies to every vertical.</p>

        <h2>2. Structural extractability</h2>

        <p>AI engines do not read your page like a human. They parse it. The easier your content is to parse cleanly into a citation-ready paragraph, the more likely it is to be surfaced. That means:</p>

        <ul>
          <li>Real heading hierarchy (H1, H2, H3 — not styled divs).</li>
          <li>Short, self-contained paragraphs.</li>
          <li>Lists where lists make sense.</li>
          <li>FAQ blocks with proper schema.</li>
          <li>A clear factual statement near the top of each section.</li>
        </ul>

        <p>This is the single most underrated optimization opportunity in AI search. Most sites are still writing for humans skimming, not machines extracting.</p>

        <h2>3. Verifiable consistency</h2>

        <p>AI engines cross-reference your business across the open web. If your name, address, services, hours, and positioning say one thing on your site and another on Yelp, Google Business, or directory listings, the engine will quietly downgrade your citation odds. This is a defensive signal you can fix in an afternoon and benefit from for years.</p>

        <h2>4. Freshness</h2>

        <p>A page from three years ago is not automatically bad — but a page from last week is automatically better, all else equal. AI engines weight recency heavily because their reputation depends on giving current answers. Continuous publishing is now a structural advantage, not a nice-to-have. This is one of the reasons automated SEO has become so impactful: it produces freshness at a cadence no manual team can match. See <A to="/blog/automated-seo-rank-more-do-less">The Power of Automated SEO</A> for the operational picture.</p>

        <h2>5. Internal linking density</h2>

        <p>Internal links do two things for AI engines: they prove topical depth (you have many connected pages on a subject), and they help the crawler map your site quickly. A page with no inbound internal links signals "isolated" — and isolated pages get cited less. A page that sits in the middle of a tight cluster of related content signals "this is part of a real body of work."</p>

        <h2>6. Citation-worthy language</h2>

        <p>AI engines prefer to quote content that reads like it could stand alone. Hedged, hype-heavy, brand-centric copy is hard to extract. Direct, factual, specific language is easy. Compare:</p>

        <p><em>"We're proud to offer industry-leading solutions tailored to your unique needs."</em></p>

        <p>versus</p>

        <p><em>"Emergency plumbing service in Brooklyn, available 24/7, with a 60-minute response window."</em></p>

        <p>The second sentence is extractable. The first is invisible to an AI engine. Write the second one.</p>

        <h2>7. Schema and structured data</h2>

        <p>This is the technical foundation everything else sits on. LocalBusiness, FAQPage, Article, Product, Service, BreadcrumbList — every page should carry the schema that matches what it is. AI engines use schema as a confidence signal that you are who you say you are.</p>

        <h2>8. Authority and trust signals</h2>

        <p>Reviews, third-party mentions, press, citations from authoritative directories — all of these still matter, and they matter more inside AI search than they did inside classic Google. The AI engine is making a reputational bet every time it cites you. The more external confirmation it can find, the more confidently it will mention your name.</p>

        <h2>9. Crawlability</h2>

        <p>An AI engine cannot cite a page it cannot reach. Blocked routes, JavaScript-heavy renders that fail without execution, slow servers, and broken sitemaps all silently cost you citations. The basics still matter — possibly more than ever.</p>

        <h2>10. Volume of indexed surface</h2>

        <p>Two businesses with equal quality but unequal volume will not get equal AI visibility. The one with hundreds of indexed pages covering the topic gets cited dramatically more often than the one with ten. This is not a vanity metric. It is the surface area through which AI engines find you, and it compounds.</p>

        <h2>How to optimize for all of this without losing your mind</h2>

        <p>If you read the list above and thought "there is no way I am doing all of that by hand," you are correct. Nobody is. The teams winning AI search today are running automated systems that produce structurally clean, topically deep, schema-rich, freshly-updated content continuously — and then steering those systems with human judgment. Read <A to="/blog/seo-automation-explained">SEO Automation Explained</A> for the honest breakdown of what should run on autopilot and what should not.</p>

        <p>This is exactly what <Aext href="/">OrbitRank</Aext> was built to do: turn the ranking factors above into a default state instead of a checklist. Every page produced is structurally extractable, topically connected to the rest of your content, schema-marked, freshness-tracked, and submitted to both Google and AI crawlers the moment it goes live.</p>

        <h2>The takeaway</h2>

        <p>AI search ranking is not a mystery. The signals are knowable, the patterns are stable enough to optimize for, and the businesses that act now will compound a lead the slow ones cannot close. To see how AI engines specifically decide which businesses make it into a citation paragraph, read <A to="/blog/getting-cited-by-ai">Getting Cited by AI: The New SEO Frontier</A>.</p>
      </>
    ),
  },
};

const ArticleHeader = () => {
  const navigate = useNavigate();
  return (
    <header className="relative z-20 max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 flex items-center justify-between h-16 md:h-20 gap-3">
      <a href="/" className="flex items-center min-w-0 flex-shrink">
        <img src={logoFull} alt="OrbitRank" className="h-7 md:h-8 w-auto object-contain" />
      </a>
      <nav className="hidden md:flex items-center gap-9">
        {NAV_LINKS.map((l) => (
          <a key={l.label} href={l.href}
            className={`text-[14px] font-medium transition-colors flex items-center gap-1 ${l.active ? "text-[#00FF88]" : "text-white/85 hover:text-white"}`}
            style={l.active ? { textShadow: "0 0 12px rgba(0,255,136,0.55)" } : undefined}>
            {l.label}
            {l.caret && <ChevronDown className="w-3.5 h-3.5 opacity-70" />}
          </a>
        ))}
      </nav>
      <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
        <button onClick={() => navigate("/auth")} className="hidden md:inline-flex text-[14px] font-medium text-white/85 hover:text-white">Log in</button>
        <button onClick={() => navigate("/activate")}
          className="inline-flex items-center gap-1.5 md:gap-2 h-9 md:h-10 px-2.5 md:px-5 rounded-lg text-[11px] md:text-[14px] font-semibold text-[#04060A] bg-[#00FF88] hover:bg-[#1aff96] transition-all whitespace-nowrap leading-none flex-shrink-0"
          style={{ boxShadow: "0 0 0 1px rgba(0,255,136,0.4), 0 8px 24px rgba(0,255,136,0.25)" }}>
          <span className="whitespace-nowrap">Launch My SEO</span>
          <ArrowRight className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
        </button>
      </div>
    </header>
  );
};

const BlogArticle = () => {
  const { slug = "" } = useParams();
  const staticArticle = ARTICLES[slug];
  const [dynamicArticle, setDynamicArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(!staticArticle);

  useEffect(() => {
    if (staticArticle) return;
    (async () => {
      const { data } = await supabase
        .from("blog_posts")
        .select("title,slug,meta_description,content,featured_image,published_at,created_at,keyword")
        .eq("client_id", ORBITRANK_CLIENT_ID)
        .eq("slug", slug)
        .maybeSingle();
      if (data && data.title) {
        const dateStr = new Date((data.published_at || data.created_at) as string).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
        const category = (typeof data.keyword === "string" && !data.keyword.startsWith("{")) ? data.keyword : "AI SEO";
        setDynamicArticle({
          slug: data.slug as string,
          category,
          accent: "#00FF88",
          title: data.title as string,
          excerpt: data.meta_description || "",
          date: dateStr,
          readTime: "5 min read",
          body: <div dangerouslySetInnerHTML={{ __html: sanitizeArticleHtml(data.content || "") }} />,
          featuredImage: (data as any).featured_image || undefined,
        } as Article);
      }
      setLoading(false);
    })();
  }, [slug, staticArticle]);


  const article = staticArticle || dynamicArticle;

  if (!article) {
    return (
      <div className="min-h-screen bg-[#03050A] text-white overflow-x-hidden w-full max-w-[100vw]" style={{ fontFamily: "Inter, ui-sans-serif, system-ui" }}>
        <ArticleHeader />
        <div className="max-w-[760px] mx-auto px-4 py-24 text-center">
          <h1 className="text-3xl font-bold mb-4">{loading ? "Loading…" : "Article not found"}</h1>
          {!loading && <p className="text-white/60 mb-8">This article doesn't exist yet.</p>}
          <Link to="/blog" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#00FF88] text-[#04060A] font-semibold">
            <ArrowLeft className="w-4 h-4" /> Back to blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#03050A] text-white overflow-x-hidden w-full max-w-[100vw]" style={{ fontFamily: "Inter, ui-sans-serif, system-ui" }}>
      <div aria-hidden className="absolute inset-x-0 top-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(0,255,136,0.4), transparent)" }} />
      <ArticleHeader />

      <article className="relative max-w-[760px] mx-auto px-3 sm:px-6 pt-6 md:pt-10 pb-16 md:pb-24">
        <Link to="/blog" className="inline-flex items-center gap-2 text-[13px] text-white/60 hover:text-white mb-6 md:mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to blog
        </Link>

        <span className="inline-block px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wider"
          style={{ background: `${article.accent}15`, color: article.accent, border: `1px solid ${article.accent}40` }}>
          {article.category}
        </span>

        <h1 className="mt-4 text-[28px] sm:text-[36px] md:text-[44px] leading-[1.15] font-bold tracking-tight">
          {article.title}
        </h1>

        <p className="mt-4 text-[15px] md:text-[18px] text-white/65 leading-relaxed">{article.excerpt}</p>

        <div className="mt-5 flex items-center gap-4 text-[12px] md:text-[13px] text-white/45 pb-6 md:pb-8 border-b border-white/10">
          <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{article.date}</span>
          <span>•</span>
          <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{article.readTime}</span>
        </div>

        {(SLUG_IMAGES[article.slug] || article.featuredImage) && (
          <div className="mt-8 md:mt-10 relative rounded-2xl overflow-hidden"
            style={{ border: `1px solid ${article.accent}33`, boxShadow: `0 30px 80px -20px ${article.accent}33, 0 0 0 1px rgba(255,255,255,0.04) inset` }}>
            <img src={SLUG_IMAGES[article.slug] || article.featuredImage!} alt={article.title}
              className="block w-full h-auto" width={1920} height={1080} />
            <div className="pointer-events-none absolute inset-0"
              style={{ background: `radial-gradient(circle at 50% 0%, ${article.accent}15, transparent 60%), linear-gradient(180deg, rgba(3,5,10,0) 70%, rgba(3,5,10,0.6) 100%)` }} />
          </div>
        )}

        <div
          className="prose-article mt-6 md:mt-10 text-white/80 text-[14.5px] md:text-[17px] leading-[1.6] md:leading-[1.75]"
        >
          {article.body}
        </div>


        <div className="mt-12 md:mt-16 p-6 md:p-8 rounded-2xl"
          style={{
            background: "linear-gradient(135deg, rgba(0,255,136,0.08), rgba(15,20,30,0.9))",
            border: "1px solid rgba(0,255,136,0.25)",
            boxShadow: "0 0 60px -20px rgba(0,255,136,0.3)",
          }}>
          <h3 className="text-[18px] md:text-[22px] font-bold leading-tight">Ready to put this into practice?</h3>
          <p className="mt-2 text-white/65 text-[14px] md:text-[15px]">OrbitRank deploys an automated SEO system for your business — discovery, content, indexing, and monitoring on autopilot.</p>
          <div className="mt-5 flex flex-wrap gap-3">
            {launchCta()}
            <Link to="/blog" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-[14px] font-semibold border"
              style={{ borderColor: "#A78BFA66", color: "#A78BFA", boxShadow: "0 0 0 1px #A78BFA22 inset" }}>
              Read more articles <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className="mt-12 md:mt-16">
          <h4 className="text-[13px] uppercase tracking-wider text-white/50 mb-4">Continue reading</h4>
          <div className="grid sm:grid-cols-2 gap-4">
            {Object.values(ARTICLES).filter((a) => a.slug !== article.slug).slice(0, 2).map((a) => (
              <Link key={a.slug} to={`/blog/${a.slug}`}
                className="block p-5 rounded-xl transition-all hover:-translate-y-1"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
                <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider"
                  style={{ background: `${a.accent}15`, color: a.accent, border: `1px solid ${a.accent}40` }}>
                  {a.category}
                </span>
                <h5 className="mt-3 text-[15px] md:text-[16px] font-semibold leading-snug">{a.title}</h5>
                <span className="mt-3 inline-flex items-center gap-1 text-[12px]" style={{ color: a.accent }}>
                  Read article <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </article>

      <style>{`
        .prose-article h2 { font-size: 20px; font-weight: 700; color: white; margin: 26px 0 10px; line-height: 1.3; }
        @media (min-width: 768px) { .prose-article h2 { font-size: 28px; margin: 48px 0 14px; } }
        .prose-article h3 { font-size: 17px; font-weight: 700; color: white; margin: 22px 0 8px; line-height: 1.35; }
        @media (min-width: 768px) { .prose-article h3 { font-size: 20px; margin: 28px 0 10px; } }
        .prose-article p { margin: 0 0 14px; }
        @media (min-width: 768px) { .prose-article p { margin: 0 0 18px; } }
        .prose-article ul { margin: 0 0 16px; padding-left: 20px; list-style: disc; }
        @media (min-width: 768px) { .prose-article ul { margin: 0 0 22px; padding-left: 22px; } }
        .prose-article li { margin-bottom: 6px; }
        @media (min-width: 768px) { .prose-article li { margin-bottom: 8px; } }

        .prose-article strong { color: white; font-weight: 700; }
        .prose-article em { color: rgba(255,255,255,0.9); font-style: italic; }
      `}</style>
    </div>
  );
};

export default BlogArticle;
